import classNames from 'classnames'
import { useRouter } from 'next/router'
import { NextSeoProps } from 'next-seo'
import { I18NConfig } from 'next/dist/server/config-shared'

import { Utils } from 'common/utils'
import { getPlans } from 'common/planData'
import { createClient } from '../prismicio'
import { PlanSlug } from 'common/planSlugs'
import Button from 'components/forms/Button'
import FAQList from 'components/util/FAQList'
import useShoppingBag from 'common/useShoppingBag'
import HeroBanner from 'components/hero/HeroBanner'
import { heroGap, sectionGap } from 'project/padding'
import { getFAQList, getReviews } from 'common/cms-sdk'
import standardiseTitle from 'project/standardiseTitle'
import useWindowSize from 'components/util/useWindowSize'
import WhoIsThisFor from 'components/cards/new/WhoIsThisFor'
import ContactUsCard from 'components/cards/new/ContactUsCard'
import ReviewsSection from 'components/sections/ReviewsSection'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import SubscriptionPricing from 'components/SubscriptionPricing'
import SeoContainer from 'components/util/containers/SeoContainer'
import RightRobotCard from 'components/cards/new/common/RightRobotCard'
import CallForwardingCosts from 'components/cards/new/CallForwardingCosts'
import { TAddon, TFAQList, TPlanSummary, TReviews } from 'common/cms-types'
import LondonVOPlusHeroCard from 'components/cards/new/LondonVOPlusHeroCard'
import EmbededNewsletterForm from 'components/sections/EmbededNewsletterForm'
import { virtualOfficeOldStreetBreadcrumb } from 'components/util/containers/Breadcrumbs'
import { planPrice } from 'common/utils/planPrice'
import planNames from '../common/planNames'

export type VOPhoneType = {
  // @ts-ignore
  plans: TPlanSummary[]
  addons: TAddon[] | null
  faqList: TFAQList
  reviews: TReviews
}
const VOPhonePage: NextPageWithLayout<VOPhoneType> = ({
  faqList,
  plans,
  reviews,
}) => {
  const router = useRouter()
  const size = useWindowSize()
  const { addPlan } = useShoppingBag()
  const planSummary = plans.find((v) => v.slug === PlanSlug.PHONE)!

  const onBuyClick = () => {
    addPlan(planSummary.slug, 'year', planSummary)
    router.push('/checkout')
  }

  const breadcrumb = virtualOfficeOldStreetBreadcrumb().concat([
    {
      item: `https://www.hoxtonmix.com/virtual-office-phone/`,
      name: `Virtual Office Phone`,
      position: 3,
    },
  ])

  const seoProps: NextSeoProps = {
    description: `Impress your clients with a dedicated virtual phone service from Hoxton Mix including unlimited calls from ${planPrice
      .monthlyOnYearlyPlan(PlanSlug.PHONE)
      .toLocaleLowerCase()} a month.`,
    openGraph: {
      url: `https://www.hoxtonmix.com/virtual-office-phone/`,
    },
    title: standardiseTitle(`Virtual Office Phone System | Hoxton Mix`),
  }

  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column',
          heroGap,
        )}
      >
        <HeroBanner
          robotImage={{
            alt: 'HaiRo™ robot mascot holding phone',
            dimensions: {
              height: 815,
              width: 704,
            },
            src: '/images/new/robot-london-020-number.png',
          }}
          hideDownloadButtons
          title={
            <>
              {size.isLgOrHigher && (
                <span className='fs-fluid-18-24 fw-semibold'>
                  {planNames[PlanSlug.PHONE]}
                </span>
              )}
              <h1 className='fs-fluid-32-64 lh-114 lh-lg-108 mb-lg-3 mb-3'>
                Virtual Office Phone — a London business number that moves with
                you
              </h1>
            </>
          }
          subtitleContainerClassName='mb-3 mb-lg-2'
          subtitle={
            <span className='fw-semibold d-block fs-fluid-20-26 lh-108 lh-lg-114'>
              Establish a professional business presence with a London virtual
              phone number.
            </span>
          }
          description={
            <>
              <span className='fw-semibold fs-fluid-16-20 lh-140 lh-lg-114 d-block pe-5'>
                A virtual phone number gives your business a{' '}
                <span className='color-pink2'>prime London 020</span> landline
                with call forwarding to any UK, EU or US number. Wherever
                you are, stay connected and ensure your business
                always looks professional.
              </span>
            </>
          }
          cta={
            <div>
              {!size.isLgOrHigher && (
                <span className='fs-fluid-18-24 fw-semibold pb-3 d-block'>
                  {planNames[PlanSlug.PHONE]}
                </span>
              )}
              <SubscriptionPricing
                buttonAlign='start'
                planSummary={plans.find((v) => v.slug === PlanSlug.PHONE)!}
                hideTitle
              />
            </div>
          }
        />

        <div
          className={classNames(
            'container d-flex flex-column gap-4 pt-lg-23',
            sectionGap,
          )}
        >
          <WhoIsThisFor />
          <RightRobotCard
            image={{
              alt: 'Robot showing enterprise solution',
              desktop: {
                dimensions: {
                  height: 627,
                  width: 757,
                },
                src: '/images/new/robot-enterprise-upperhalf.png',
              },
              mobile: {
                dimensions: {
                  height: 627,
                  width: 757,
                },
                src: '/images/new/robot-enterprise-upperhalf-cut.png',
              },
            }}
            imageContainerWidth={size.isLgOrHigher ? '55%' : '100%'}
            imageStyle={{
              maxHeight: '100%',
              width: '100%',
            }}
          >
            <div className='d-flex flex-column px-4 ps-lg-20 pt-9 py-lg-31 flex-1 justify-content-center me-lg-n14'>
              <h2 className='fs-fluid-36-80 lh-105 fw-semibold mb-lg-3'>
                How it Works
              </h2>
              <span className='fs-fluid-18-28 lh-119 fw-semibold'>
                Easy Setup – No Special Tech Required
              </span>
              <div className='d-flex flex-column fs-fluid-20-28 lh-119 fw-semibold gap-5 gap-lg-7 mt-5 mt-lg-5'>
                <span>
                  <span className='color-pink2'>1 </span> Pick Your London 020
                  Number.
                </span>
                <span>
                  <span className='color-pink2'>2 </span> Choose Where to
                  Forward Calls.
                </span>
                <span>
                  <span className='color-pink2'>3 </span> Start Answering Calls
                  Instantly.
                </span>
              </div>
              <Button
                onClick={onBuyClick}
                className='w-100 w-lg-auto align-self-start mt-8'
              >
                Get Started Now
              </Button>
            </div>
          </RightRobotCard>
          <CallForwardingCosts />
          <FAQList
            title={faqList.data.title}
            subtitle={faqList.data.subtitle}
            slices={faqList.data.slices}
            expandAllButton
            expandAllButtonDesktopOnly
          />
          <RightRobotCard
            image={{
              alt: 'Robot demonstrating never miss a call',
              desktop: {
                dimensions: {
                  height: 683,
                  width: 583,
                },
                src: '/images/new/robot-never-miss-call.png',
              },
              mobile: {
                dimensions: {
                  height: 683,
                  width: 583,
                },
                src: '/images/new/robot-never-miss-call.png',
              },
            }}
            imageContainerWidth={size.isLgOrHigher ? '45%' : '100%'}
            overrideAllImageStyles
            imageStyle={{
              height: '100%',
              maxWidth: '100%',
              objectFit: 'contain',
            }}
          >
            <div className='d-flex flex-column flex-1 px-4 ps-lg-21 pt-9 pt-13 pb-lg-50 pb-xl-87 gap-6 me-lg-n17 me-xxl-n30'>
              <h2 className='fs-fluid-36-80 lh-105 fw-semibold mb-0'>
                Take Control of Your Business Calls Today
              </h2>
              <span className='fs-fluid-20-28 lh-114 fw-semibold'>
                Easily manage your call forwarding via our online dashboard.
                <span className='color-pink2'>
                  {' '}
                  Update your forwarding settings anytime
                </span>
                , monitor call logs, and stay in control of your business
                communications.
              </span>
            </div>
          </RightRobotCard>

          <div className='row gy-5'>
            <div className='col-12 col-lg-6 '>
              <ContactUsCard />
            </div>
            <div className='col-12 col-lg-6'>
              <LondonVOPlusHeroCard
                planSummary={plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!}
              />
            </div>
          </div>
          <EmbededNewsletterForm />
        </div>
        <ReviewsSection data={reviews} plan={PlanSlug.PHONE} pageUrl={router.pathname} />
      </main>
    </SeoContainer>
  )
}

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)

    const plans = await getPlans()
    const reviews = await getReviews()
    const faqList = await getFAQList(client, lang, 'virtual-office-phone')

    return {
      props: {
        faqList,
        plans,
        reviews,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}

export default VOPhonePage
